import { MAP_LAT_BOTTOM, MAP_LAT_TOP } from './worldMapDots.generated';
import { LAND_DOTS, type NormalizedPoint } from './worldMapDots';

export interface DanceCity {
  name: string;
  lon: number;
  lat: number;
}

/**
 * Scenes the pings fire in. Real cities with real dance communities — this is
 * the whole reason the backdrop is a map and not an abstract particle field.
 */
export const DANCE_CITIES: readonly DanceCity[] = [
  { name: 'Austin', lon: -97.74, lat: 30.27 },
  { name: 'New York', lon: -74.0, lat: 40.71 },
  { name: 'Los Angeles', lon: -118.24, lat: 34.05 },
  { name: 'Mexico City', lon: -99.13, lat: 19.43 },
  { name: 'Bogotá', lon: -74.07, lat: 4.71 },
  { name: 'São Paulo', lon: -46.63, lat: -23.55 },
  { name: 'Buenos Aires', lon: -58.38, lat: -34.6 },
  { name: 'Lisbon', lon: -9.14, lat: 38.72 },
  { name: 'Madrid', lon: -3.7, lat: 40.42 },
  { name: 'Paris', lon: 2.35, lat: 48.86 },
  { name: 'London', lon: -0.13, lat: 51.51 },
  { name: 'Berlin', lon: 13.4, lat: 52.52 },
  { name: 'Amsterdam', lon: 4.9, lat: 52.37 },
  { name: 'Rome', lon: 12.5, lat: 41.9 },
  { name: 'Kyiv', lon: 30.5, lat: 50.45 },
  { name: 'Luanda', lon: 13.23, lat: -8.84 },
  { name: 'Lagos', lon: 3.38, lat: 6.52 },
  { name: 'Cape Town', lon: 18.42, lat: -33.93 },
  { name: 'Cairo', lon: 31.24, lat: 30.04 },
  { name: 'Tokyo', lon: 139.69, lat: 35.69 },
  { name: 'Shanghai', lon: 121.47, lat: 31.23 },
  { name: 'Sydney', lon: 151.21, lat: -33.87 },
  { name: 'Dubai', lon: 55.27, lat: 25.2 },
  { name: 'Mumbai', lon: 72.87, lat: 19.08 },
];

/** Longitude/latitude to normalized map space, matching the generator's crop. */
export function projectToMap(lon: number, lat: number): NormalizedPoint {
  return {
    x: (lon + 180) / 360,
    y: (MAP_LAT_TOP - lat) / (MAP_LAT_TOP - MAP_LAT_BOTTOM),
  };
}

/**
 * Index of the land dot closest to a coordinate. Coastal cities sit just off
 * the lattice, so pings snap to land instead of firing over open water.
 */
export function nearestDotIndex(
  lon: number,
  lat: number,
  dots: readonly NormalizedPoint[] = LAND_DOTS
): number {
  const target = projectToMap(lon, lat);
  let best = 0;
  // Comparing SQUARED distances: sqrt is monotonic, so it cannot change which
  // dot is nearest, and skipping it avoids 3317 square roots per city.
  let bestSquared = Infinity;

  dots.forEach((dot, i) => {
    const squared = (dot.x - target.x) ** 2 + (dot.y - target.y) ** 2;
    if (squared < bestSquared) {
      bestSquared = squared;
      best = i;
    }
  });

  return best;
}

/** Resolved once at module load; the render loop only ever reads indexes. */
export const DANCE_CITY_DOT_INDEXES: readonly number[] = DANCE_CITIES.map((city) =>
  nearestDotIndex(city.lon, city.lat)
);
